/**
 * The native ribbon is a second set of controls for the mapping workspace, so a
 * ribbon press must reach exactly the same guarded action as the embedded
 * control it mirrors — never a private shortcut into workspace state.
 *
 * This module turns one ribbon command into that action. The hook that owns the
 * window slot keeps only installation and ownership.
 */

import type { SettingsType } from '../../../shared/api/pipeex/pcfSettingsClient';
import type { HostRibbonCommand } from '../../../shared/lib/hostRibbon';
import { openRuntimeVersionInfo } from '../../../shared/ui/feedback/RuntimeVersionInfo';
import {
  requestGuardedProfileChange,
  requestGuardedProfileLoad,
} from './guardedProfileNavigation';
import { normalizeHostRibbonWorkspaceTab, parseHostRibbonProfileKeys } from './mappingPageHostBridge';
import {
  buildSettingInfoProfileKey,
  getProfileKeyParts,
  parseMappingSettingsType,
} from './mappingPageProjectProfileUtils';
import { toTraceKey, traceMappingFlow, warnMappingFlow } from './mappingTrace';
import { resolveRequestedWorkspaceTab, type MappingWorkspaceTabIndex } from './mappingWorkspacePageModel';
import type { NavigationSource } from '../../../shared/lib/setupRevision';

/** Every ribbon profile command is attributed to the native route it came from. */
const HostRibbonProfileSource: NavigationSource = 'hostRibbonProfile';

export interface MappingHostRibbonProfile {
  name: string;
  settingsType: SettingsType;
}

export interface MappingHostRibbonCommandDeps {
  /** Whether this route may still act on ribbon clicks at all. */
  ownsHostRibbon: () => boolean;
  requestWorkspaceTabChange: (tab: MappingWorkspaceTabIndex, source: NavigationSource) => void;
  selectedProfileKey: string;
  lastAppliedProfileKey: string;
  profiles: MappingHostRibbonProfile[];
  profilesLoading: boolean;
  isSaving: boolean;
  profileLoadDisabled: boolean;
  /** Set while a load raised by this bridge has not settled yet. */
  loadInFlight: { current: boolean };
  /** Raw state changes — only ever called from inside a guarded commit. */
  applySelectedSettingsType: (value: SettingsType) => void;
  applySelectedProfileChange: (profileKey: string) => void;
  loadProfileForCurrentProject: (profileKeyOverride?: string) => Promise<boolean>;
  /** The destination-specific consent the guard asks for before committing. */
  confirmProfileLoad: (profileKeyOverride?: string) => Promise<boolean>;
  onRefreshProfiles: () => void;
  /** Publishes one profileDetails message for the native details window. */
  onRequestProfileDetails: (profileKey: string) => void;
  onSaveProfile: () => void;
  onDeleteProfile: (profileKey?: string) => void;
  onDeleteProfiles: (profileKeys: string[]) => void;
  handleOpenActivePageGuide: () => void;
  handleOpenActivePageTour: () => void;
  handleOpenSummaryWindow: () => void;
  handleSignOut: () => Promise<void>;
  toggleTheme: () => void;
  setHostProfileSaveAsDialogOpen: (open: boolean) => void;
  setHostProfileRenameDialogProfileKey: (profileKey: string | null) => void;
}

/** The profile the ribbon's scope switch should land on, or '' when none fits. */
export const resolveProfileScopeSelection = (
  profiles: MappingHostRibbonProfile[],
  selectedProfileKey: string,
  settingsType: SettingsType
): string => {
  const [currentName] = selectedProfileKey.split('::');
  const inScope = (profile: MappingHostRibbonProfile): boolean =>
    parseMappingSettingsType(profile.settingsType) === settingsType;
  const matchingProfile =
    profiles.find((profile) => inScope(profile) && profile.name === currentName) ??
    profiles.find(inScope);

  return matchingProfile ? buildSettingInfoProfileKey(matchingProfile) : '';
};

/**
 * Scope and profile move together, so both raw setters run inside one commit:
 * two separate requests would leave the second superseded by the first.
 */
const requestProfileSelection = (
  deps: MappingHostRibbonCommandDeps,
  profileKey: string,
  settingsType: SettingsType
): void => {
  void requestGuardedProfileChange(profileKey, HostRibbonProfileSource, () => {
    deps.applySelectedSettingsType(settingsType);
    deps.applySelectedProfileChange(profileKey);
  });
};

/** Why this load may not start now, or null when it may. */
const resolveLoadSuppression = (deps: MappingHostRibbonCommandDeps): string | null => {
  if (deps.profileLoadDisabled) {
    return 'profile operation active';
  }
  if (deps.isSaving) {
    return 'workspace save active';
  }
  if (deps.profilesLoading) {
    return 'profile catalog loading';
  }

  return deps.loadInFlight.current ? 'profile load already in flight' : null;
};

const runProfileLoad = async (
  deps: MappingHostRibbonCommandDeps,
  profileKey: string,
  startedAt: number
): Promise<void> => {
  const profileTraceKey = toTraceKey(profileKey);

  try {
    // Reloading the profile that is already applied replaces nothing, so it
    // needs no consent — the guard still decides whether it may proceed.
    const reloadingApplied = profileKey === deps.lastAppliedProfileKey;
    const loaded = await requestGuardedProfileLoad(
      profileKey,
      HostRibbonProfileSource,
      () => deps.loadProfileForCurrentProject(profileKey),
      reloadingApplied ? undefined : { confirm: () => deps.confirmProfileLoad(profileKey) }
    );

    traceMappingFlow('mapping profile load command complete', {
      source: 'host ribbon',
      profileKey: profileTraceKey,
      loaded,
      elapsedMs: Date.now() - startedAt,
    });
  } catch (error) {
    warnMappingFlow('mapping profile load command failed', {
      source: 'host ribbon',
      profileKey: profileTraceKey,
      elapsedMs: Date.now() - startedAt,
      errorType: error instanceof Error ? error.name : typeof error,
      message: error instanceof Error ? error.message : 'Profile load failed.',
    });
    throw error;
  } finally {
    deps.loadInFlight.current = false;
    traceMappingFlow('mapping profile load command released', {
      source: 'host ribbon',
      profileKey: profileTraceKey,
      elapsedMs: Date.now() - startedAt,
    });
  }
};

const requestProfileLoad = (deps: MappingHostRibbonCommandDeps, value?: string | null): void => {
  const suppressionReason = resolveLoadSuppression(deps);
  if (suppressionReason) {
    traceMappingFlow('mapping profile load command suppressed', {
      source: 'host ribbon',
      reason: suppressionReason,
    });
    return;
  }

  const profileKey = value?.trim() || deps.selectedProfileKey;
  if (!profileKey) {
    traceMappingFlow('mapping profile load command suppressed', {
      source: 'host ribbon',
      reason: 'no profile selected',
    });
    return;
  }

  deps.loadInFlight.current = true;
  traceMappingFlow('mapping profile load command accepted', {
    source: 'host ribbon',
    profileKey: toTraceKey(profileKey),
    alreadyApplied: profileKey === deps.lastAppliedProfileKey,
  });
  // Nobody awaits a ribbon press, so a failed load is already reported by
  // runProfileLoad; catching here only keeps it from becoming an unhandled
  // rejection the host shell would surface as an unexplained error.
  void runProfileLoad(deps, profileKey, Date.now()).catch(() => undefined);
};

/** Ribbon commands that change the selected profile or the workspace tab. */
const dispatchNavigationCommand = (
  deps: MappingHostRibbonCommandDeps,
  payload: HostRibbonCommand
): boolean => {
  switch (payload.command) {
    case 'setWorkspaceTab': {
      const tab = resolveRequestedWorkspaceTab(normalizeHostRibbonWorkspaceTab(payload.value));
      if (tab !== null) {
        deps.requestWorkspaceTabChange(tab, 'hostRibbonTab');
      }
      return true;
    }
    case 'selectProfile': {
      const profileKey = payload.value?.trim();
      if (profileKey) {
        requestProfileSelection(deps, profileKey, getProfileKeyParts(profileKey).settingsType);
      }
      return true;
    }
    case 'setProfileScope': {
      const settingsType = parseMappingSettingsType(payload.value);
      requestProfileSelection(
        deps,
        resolveProfileScopeSelection(deps.profiles, deps.selectedProfileKey, settingsType),
        settingsType
      );
      return true;
    }
    case 'loadProfile':
      requestProfileLoad(deps, payload.value);
      return true;
    default:
      return false;
  }
};

/** Ribbon commands that manage the profile catalog without leaving the setup. */
const dispatchProfileCatalogCommand = (
  deps: MappingHostRibbonCommandDeps,
  payload: HostRibbonCommand
): boolean => {
  switch (payload.command) {
    case 'refreshProfiles':
      deps.onRefreshProfiles();
      return true;
    case 'saveProfile':
      deps.onSaveProfile();
      return true;
    case 'saveProfileAs':
      if (payload.value !== null && payload.value !== undefined) {
        deps.applySelectedSettingsType(parseMappingSettingsType(payload.value));
      }
      deps.setHostProfileSaveAsDialogOpen(true);
      return true;
    case 'renameProfile':
      deps.setHostProfileRenameDialogProfileKey(payload.value?.trim() || null);
      return true;
    case 'deleteProfile':
      deps.onDeleteProfile(payload.value ?? undefined);
      return true;
    case 'deleteProfiles': {
      const profileKeys = parseHostRibbonProfileKeys(payload.value);
      if (profileKeys.length > 0) {
        deps.onDeleteProfiles(profileKeys);
      }
      return true;
    }
    default:
      return false;
  }
};

/** Ribbon commands that open a window or change a shell-level preference. */
const dispatchShellCommand = (
  deps: MappingHostRibbonCommandDeps,
  payload: HostRibbonCommand
): void => {
  switch (payload.command) {
    case 'openGuide':
      deps.handleOpenActivePageGuide();
      return;
    case 'startTour':
      deps.handleOpenActivePageTour();
      return;
    case 'openSummary':
      deps.handleOpenSummaryWindow();
      return;
    case 'requestProfileDetails':
      deps.onRequestProfileDetails(payload.value?.trim() || deps.selectedProfileKey);
      return;
    case 'openVersions':
      openRuntimeVersionInfo();
      return;
    case 'toggleTheme':
      deps.toggleTheme();
      return;
    case 'signOut':
      void deps.handleSignOut();
      return;
    default:
  }
};

export const dispatchMappingHostRibbonCommand = (
  deps: MappingHostRibbonCommandDeps,
  payload: HostRibbonCommand
): void => {
  if (!deps.ownsHostRibbon()) {
    traceMappingFlow('mapping host ribbon command ignored by superseded owner', {
      command: payload.command,
    });
    return;
  }

  if (dispatchNavigationCommand(deps, payload) || dispatchProfileCatalogCommand(deps, payload)) {
    return;
  }

  dispatchShellCommand(deps, payload);
};
